// useAuth — JWT w localStorage, prosty singleton listener.
//
// API:
//   const { token, team, isLoggedIn, isAdmin, login, logout } = useAuth();

import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config.js';

const STORAGE_KEY = 'fiberops.session.v1';

// In-memory cache + pub/sub
let current = loadFromStorage();
const listeners = new Set();

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveToStorage(session) {
  if (!session) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

function notify() {
  for (const l of listeners) {
    try { l(current); } catch {}
  }
}

export function getCurrentSession() {
  return current;
}

export function getToken() {
  return current?.token || null;
}

export function setSession(session) {
  current = session;
  saveToStorage(session);
  notify();
}

export function clearSession() {
  setSession(null);
}

export function useAuth() {
  const [session, setSessionState] = useState(current);

  useEffect(() => {
    const listener = (s) => setSessionState(s);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const login = useCallback(async (pin) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Login failed (${res.status})`);
    }
    const data = await res.json();
    setSession({ token: data.token, team: data.team, loginAt: Date.now() });
    return data;
  }, []);

  // K19 (Martin): admin login — osobny endpoint /api/auth/admin/login z bcrypt PIN.
  const adminLogin = useCallback(async (pin) => {
    const res = await fetch(`${API_BASE}/api/auth/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Admin login failed (${res.status})`);
    }
    const data = await res.json();
    setSession({ token: data.token, team: data.team, loginAt: Date.now() });
    return data;
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, []);

  // FAZA 2: rola z body logowania (team.role). isAdmin pozostaje derived (wstecz-zgodne).
  // Fallback dla starych sesji bez .role: admin→'admin', technik→'technik'.
  const role = session?.team?.role || (session?.team?.isAdmin ? 'admin' : (session?.token ? 'technik' : null));

  return {
    token: session?.token || null,
    team: session?.team || null,
    isLoggedIn: !!session?.token,
    isAdmin: !!session?.team?.isAdmin,
    role,
    isViewerAdmin: role === 'viewer-admin',
    // KROK 1/R2b: role serwisowe (support/support-plus) — gate'y toru wysyłkowego.
    isServiceRole: role === 'support' || role === 'support-plus',
    // AK E1 (MONIT 2026-10-10_1515): rodzaj teamu technika z body logowania ('mo' | 'ak');
    // stara sesja bez kind = montaż. Admin nie ma rodzaju (tryb MO / AK u Admina = E5).
    kind: session?.team?.isAdmin ? null : (session?.team?.kind || (session?.token ? 'mo' : null)),
    isAk: !session?.team?.isAdmin && session?.team?.kind === 'ak',
    login,
    adminLogin,
    logout,
  };
}
